import { BASE_URL } from '../constants/api';
import Fetch, { getCsrf } from './fetch';

import { Emit } from '../components/socket';

import Store from '../stores/Store';

import { GlobalState } from '../states/global-state';

import type {
  KycLoadResponseType,
  UploadResponseType,
  GetCustomerDocumentType,
  SettlementResponseType,
  SettlementLoadDataType,
  SaveCustomerAnswerType,
  CompletenesResponseType,
  CompletenesResponseDataType,
} from 'src/types/types';
import { route } from 'preact-router';
import CacheService from '../utils/CacheService';
import { hideSpinner, showAlert, showSpinner } from '../utils';

export const saveOrUpdateCustomerAnswer = async function (data: {
  batch: string;
  questionaireId: string;
  others?: string;
}): Promise<SaveCustomerAnswerType> {
  try {
    const store = new Store();
    const token = store.get('token');

    const params = new URLSearchParams();
    Object.keys(data).forEach(key => {
      params.append(key, data[key]);
    });
    params.append('token', token);

    showSpinner();

    const url = `${BASE_URL}/api/kycApi/saveOrUpdateCustomerAnswer?${params.toString()}`;

    const json = await Fetch<SaveCustomerAnswerType>(url, {
      method: 'POST',
      headers: {
        'x-token': store.get('x-token'),
        'x-csrf': store.get('x-csrf'),
        accept: 'application/x-www-form-urlencoded',
      },
      body: JSON.stringify({}),
    });

    if (json.code == 0) {
      throw new Error(json.info);
    }

    hideSpinner();

    showAlert({
      title: 'Success',
      icon: 'success',
      text: json.info,
    });

    return json;
  } catch (err) {
    hideSpinner();

    await showAlert({
      title: 'Error',
      text: err.message,
      icon: 'error',
    });
  }
};

export const completenessPercentage =
  async function (): Promise<CompletenesResponseDataType> {
    const store = new Store();
    const token = store.get('token');

    const url = `${BASE_URL}/api/customersApi/completenessPercentage?token=${token}`;

    const json = await Fetch<CompletenesResponseType>(
      url,
      {
        method: 'POST',
        headers: {
          'x-token': store.get('x-token'),
          'x-csrf': store.get('x-csrf'),
          accept: 'application/json',
        },
        body: JSON.stringify({}),
      },
      0
    );

    if (json.code == 1) {
      return json.data as CompletenesResponseDataType;
    }
    return null;
  };

export const load = async function (): Promise<KycLoadResponseType> {
  const store = new Store();
  const token = store.get('token');
  const email = store.get('email');

  const url = `${BASE_URL}/api/kycApi/load?token=${token}&email=${email}`;

  await showSpinner();

  const json = await CacheService(
    'kycApi/load',
    async () => {
      const json = await Fetch<KycLoadResponseType>(url, {
        method: 'POST',
        headers: {
          'x-token': store.get('x-token'),
          'x-csrf': store.get('x-csrf'),
          accept: 'application/json',
        },
        body: JSON.stringify({}),
      });

      return json;
    },
    { expiry: 10 }
  );

  await hideSpinner();

  return json;
};

export const upload = async function (type, blob): Promise<UploadResponseType> {
  try {
    const store = new Store();
    const token = store.get('token');

    const fd = new FormData();
    fd.append(type, blob);

    const url = `${BASE_URL}/api/upload/customerDocument/uploadByCustomer?token=${token}&type=${type}`;

    await showSpinner();
    const json = await Fetch<UploadResponseType>(url, {
      method: 'POST',
      headers: {
        'x-reg-token': store.get('x-reg-token'),
        'x-token': store.get('x-token'),
        'x-csrf': store.get('x-csrf'),
        accept: 'application/json',
      },
      body: fd,
    });

    await hideSpinner();

    await showAlert({
      title: 'Upload',
      text: json.info,
      icon: 'success',
    });

    return json;
  } catch (err) {
    await hideSpinner();
    await showAlert({
      title: 'Upload Error',
      text: err.message,
      icon: 'error',
    });
  }
};

export const getCustomerDocument = async function (
  type
): Promise<GetCustomerDocumentType> {
  const store = new Store();
  const token = store.get('token');

  const url = `${BASE_URL}/api/v1/customerDocument/docs?token=${token}&code=${type}`;

  const json = await Fetch<GetCustomerDocumentType>(url, {
    method: 'GET',
    headers: {
      'x-reg-token': store.get('x-reg-token'),
      'x-token': store.get('x-token'),
      'x-csrf': store.get('x-csrf'),
      accept: 'application/json',
    },
  });
  if (json.file) {
    json.file = `${BASE_URL}${json.file}`;
    return json;
  }
  return null;
};

export const getCustomerFile = async function (url) {
  try {
    const store = new Store();
    const token = store.get('token');

    const csrf = await getCsrf();

    const response = await fetch(url, {
      headers: {
        'x-reg-token': store.get('x-reg-token'),
        'x-token': store.get('x-token'),
        'x-csrf': csrf,
      },
    });

    const buffer = response.arrayBuffer();

    return buffer;
  } catch (err) {
    showAlert({
      icon: 'error',
      title: 'Expired Session',
      text: 'Your session has expired, please relogin again.',
      allowOutsideClick: false,
    });
  }
};

export const saveKyc = async <TData>(data): Promise<TData> => {
  const store = new Store();
  const token = store.get('token');
  data.token = token;

  return new Promise((res, rej) => {
    GlobalState.set.showSpinner(true);
    showSpinner();
    Emit('saveKyc', data, response => {
      hideSpinner();
      res(response);
    });
  });
};

export const loadSettlement =
  async function (): Promise<SettlementLoadDataType> {
    const store = new Store();
    const token = store.get('token');
    const email = store.get('email');

    const url = `${BASE_URL}/api/kycApi/loadSettlementInformation?token=${token}&email=${email}`;

    await showSpinner();

    const json = await CacheService(
      'kycApi/loadSettlementInformation',
      async () => {
        const json = await Fetch<SettlementResponseType>(url, {
          method: 'POST',
          headers: {
            'x-token': store.get('x-token'),
            'x-csrf': store.get('x-csrf'),
            accept: 'application/json',
          },
          body: JSON.stringify({}),
        });

        return {
          settlementAccountId: json.settlementAccountId,
          bankId: json.id,
          bankName: json.bankName,
          bankCode: json.bankCode,
          branchName: json.branchName,
          branchId: json.branchID,
          settlementAccountNo: json.settlementAccountNo,
          settlementAccountName: json.settlementAccountName,
          typeOfAccount: json.typeOfAccount,
        };
      },
      { expiry: 10 }
    );
    await hideSpinner();

    return json;
  };
